import React, { useState, useEffect } from 'react';
import { User, Lock } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import MaskedInput from '../../components/common/MaskedInput';
import Icon from '../../components/common/Icon';

const ResellerProfile = () => {
  const { user, updateUser } = useAuth();
  const { success, error } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return error('Nome é obrigatório');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return error('Email inválido');

    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone || null,
    };

    if (showPassword && passwordData.newPassword) {
      if (!passwordData.currentPassword) return error('Senha atual é obrigatória');
      if (passwordData.newPassword.length < 6) return error('Nova senha: mínimo 6 caracteres');
      if (passwordData.newPassword !== passwordData.confirmPassword)
        return error('As senhas não coincidem');
      payload.currentPassword = passwordData.currentPassword;
      payload.newPassword = passwordData.newPassword;
    }

    setLoading(true);
    try {
      const res = await updateUser(payload);
      if (res.success) {
        success('Perfil atualizado!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPassword(false);
      } else {
        error(res.error || 'Erro ao salvar');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <Card className="p-8 text-center text-app-text-secondary">Carregando…</Card>;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-extrabold text-app-text mb-6">Meu perfil</h1>

      <form onSubmit={submit} className="space-y-4">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon as={User} size="md" className="text-primary" />
            <h2 className="text-lg font-bold text-app-text">Informações pessoais</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome completo"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <Input
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <MaskedInput
              label="Telefone"
              name="phone"
              mask="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(11) 98765-4321"
            />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon as={Lock} size="md" className="text-primary" />
              <h2 className="text-lg font-bold text-app-text">Senha</h2>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowPassword((v) => !v);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              }}
            >
              {showPassword ? 'Cancelar' : 'Alterar senha'}
            </Button>
          </div>

          {showPassword ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t border-app-border">
              <Input
                label="Senha atual"
                name="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
              />
              <Input
                label="Nova senha"
                name="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                helpText="Mínimo 6 caracteres"
                required
              />
              <Input
                label="Confirmar"
                name="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>
          ) : (
            <p className="text-sm text-app-text-secondary">
              Clique em "Alterar senha" pra redefinir.
            </p>
          )}
        </Card>

        <div className="flex justify-end">
          <Button type="submit" loading={loading} size="lg">
            Salvar alterações
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ResellerProfile;
